import {ReactNode, isValidElement} from "react";
import {useTranslation} from "react-i18next";
import {
    RenderableDocument,
    DibJsonNode,
    ElementDataReferences,
    ReferenceFlags,
    ReferenceFlagsTo,
} from "../types";
import {getSectionHeading} from "../contentUtils";
import {usePanel} from "../panel/PanelContext";
import PaperclipIcon from "./icons/attachment.svg?react";
import WorldIcon from "./icons/world.svg?react";
import MirrorIcon from "./icons/mirror.svg?react";
import CommentTextIcon from "./icons/comment-text.svg?react";
import classes from "./SectionIconLinksLEGACY.module.css";

type Props = {
    node: DibJsonNode;
    topicData: RenderableDocument;
    renderedChildren: ReactNode[];
};

const referenceSvgs: ReferenceFlagsTo<React.FC<React.SVGProps<SVGSVGElement>>> =
    {
        reference: PaperclipIcon,
        comment: CommentTextIcon,
        translation: WorldIcon,
        mirror: MirrorIcon,
    };

const referenceI18nKeys: ReferenceFlagsTo<string> = {
    reference: "content.relatedSources",
    comment: "content.comment",
    translation: "content.translation",
    mirror: "content.lawRulesMirror",
};

export const SectionIconLinksLEGACY = ({
    node,
    topicData,
    renderedChildren,
}: Props) => {
    const {openPanel} = usePanel();
    const {t} = useTranslation();

    const elementId = node.element?.attributes?.id ?? "";
    const sectionHeading = getSectionHeading(
        topicData.elements,
        node.element?.children
    );
    const references =
        (node.elementData as ElementDataReferences | null)?.references ?? 0;
    const resourceId = topicData.id;

    const buttons = (
        Object.keys(ReferenceFlags) as Array<keyof typeof ReferenceFlags>
    ).reduce<ReactNode[]>((acc, key) => {
        const flag = ReferenceFlags[key];
        if (!(references & flag)) return acc;

        const SvgIcon = referenceSvgs[key];
        const label = t(referenceI18nKeys[key]) || key;

        const handleClick = () => {
            if (flag > 1) {
                openPanel({
                    label,
                    fetchType: "documentParts",
                    params: {
                        resourceId,
                        segmentId: `related/${flag}`,
                        id: elementId,
                    },
                });
            } else {
                openPanel({
                    label: sectionHeading || label,
                    fetchType: "inlineRelations",
                    params: {resourceId, id: elementId},
                });
            }
        };

        acc.push(
            <div className={classes.buttonContainer} key={key}>
                <button
                    type="button"
                    className={classes.iconButton}
                    onClick={handleClick}
                    aria-label={label}
                >
                    <SvgIcon />
                    <span>{label}</span>
                </button>
            </div>
        );
        return acc;
    }, []);

    if (node.kind === "level") {
        // Find the H1–H6 child by React element type. Restricting to H
        // elements (rather than "first non-blank child") prevents the
        // header wrapper from latching onto a non-heading sibling such as
        // a Stikkord container if augmentation order is ever shuffled.
        const HEADING_TYPES = new Set(["h1", "h2", "h3", "h4", "h5", "h6"]);
        const headingIdx = renderedChildren.findIndex(
            (child) =>
                isValidElement(child) &&
                typeof child.type === "string" &&
                HEADING_TYPES.has(child.type)
        );

        if (headingIdx !== -1) {
            return (
                <>
                    {renderedChildren.slice(0, headingIdx)}
                    <header className={classes.sectionHeader}>
                        {renderedChildren[headingIdx]}
                        <div className={classes.headerOptions}>{buttons}</div>
                    </header>
                    {renderedChildren.slice(headingIdx + 1)}
                </>
            );
        }

        // No heading child: fall through to the augmentedContent layout
        // so no `<header>` is emitted around a non-heading element.
    }

    return (
        <div className={classes.augmentedContent}>
            <div className={classes.contentContainer}>{renderedChildren}</div>
            <div className={classes.headerOptions}>{buttons}</div>
        </div>
    );
};
